import type { Property, Unit } from './property'
import type { Profile } from './user'

export type ProspectStatus =
  | 'lead'
  | 'viewing'
  | 'application'
  | 'approved'
  | 'rejected'
  | 'withdrawn'
  | 'converted'

export type EmploymentStatus =
  | 'employed_full_time'
  | 'employed_part_time'
  | 'self_employed'
  | 'retired'
  | 'student'
  | 'unemployed'
  | 'other'

export type ProspectReferralSource =
  | 'rightmove'
  | 'zoopla'
  | 'openrent'
  | 'spareroom'
  | 'word_of_mouth'
  | 'social_media'
  | 'website'
  | 'other'

export type ViewingStatus = 'scheduled' | 'confirmed' | 'declined' | 'completed' | 'cancelled' | 'no_show' | 'rescheduled'
export type ViewingOutcome = 'interested' | 'considering' | 'not_interested' | 'pending'

export type ApplicationStatus =
  | 'submitted'
  | 'under_review'
  | 'referencing'
  | 'approved'
  | 'rejected'
  | 'withdrawn'

export type ReferencingProvider =
  | 'openrent'
  | 'letting_works'
  | 'homelet'
  | 'rightmove'
  | 'goodlord'
  | 'vouch'
  | 'manual'
  | 'other'

export type ReferencingStatus = 'pending' | 'in_progress' | 'passed' | 'failed' | 'requires_guarantor'
export type CheckStatus = 'pending' | 'passed' | 'failed' | 'na'

export type ReferencingDocumentType =
  | 'id_passport'
  | 'id_driving_licence'
  | 'id_other'
  | 'proof_of_address'
  | 'bank_statement'
  | 'payslip'
  | 'employment_contract'
  | 'landlord_reference'
  | 'credit_report'
  | 'right_to_rent'
  | 'other'

export type PropertyInterestLevel =
  | 'enquired'
  | 'viewing_requested'
  | 'viewing_scheduled'
  | 'applied'
  | 'offered'

export interface Prospect {
  id: string
  owner_id: string
  full_name: string
  email: string
  phone: string
  budget_min_pence: number | null
  budget_max_pence: number | null
  preferred_move_in_date: string | null
  employment_status: EmploymentStatus | null
  current_address: string | null
  reason_for_moving: string | null
  referral_source: ProspectReferralSource | null
  referral_source_other: string | null
  status: ProspectStatus
  user_id: string | null
  notes: string | null
  converted_tenancy_id: string | null
  converted_at: string | null
  created_at: string
  updated_at: string
  // Joined
  user?: Pick<Profile, 'id' | 'full_name' | 'email' | 'phone'>
  viewings?: ProspectViewing[]
  applications?: ProspectApplication[]
  property_interests?: ProspectPropertyInterest[]
}

export interface ProspectPropertyInterest {
  id: string
  prospect_id: string
  property_id: string
  unit_id: string | null
  interest_level: PropertyInterestLevel
  notes: string | null
  created_at: string
  updated_at: string
  // Joined
  property?: Property
  unit?: Unit
}

export interface ProspectViewing {
  id: string
  prospect_id: string
  property_id: string
  unit_id: string | null
  scheduled_at: string
  duration_minutes: number
  status: ViewingStatus
  outcome: ViewingOutcome | null
  landlord_notes: string | null
  prospect_feedback: string | null
  completed_at: string | null
  confirmed_at: string | null
  declined_at: string | null
  declined_reason: string | null
  created_at: string
  updated_at: string
  // Joined
  prospect?: Pick<Prospect, 'id' | 'full_name' | 'email' | 'phone' | 'user_id'>
  property?: Property
  unit?: Unit
}

export interface ProspectApplication {
  id: string
  prospect_id: string
  property_id: string
  unit_id: string | null
  status: ApplicationStatus
  proposed_rent_pence: number | null
  proposed_move_in_date: string | null
  proposed_term_months: number | null
  landlord_notes: string | null
  rejection_reason: string | null
  submitted_at: string
  reviewed_at: string | null
  decided_at: string | null
  created_at: string
  updated_at: string
  // Joined
  prospect?: Pick<Prospect, 'id' | 'full_name' | 'email' | 'phone' | 'user_id'>
  property?: Property
  unit?: Unit
  referencing?: ProspectReferencing
}

export interface ProspectReferencing {
  id: string
  application_id: string
  provider: ReferencingProvider | null
  provider_other: string | null
  external_reference: string | null
  external_status: string | null
  external_status_updated_at: string | null
  status: ReferencingStatus
  credit_check_status: CheckStatus | null
  credit_check_notes: string | null
  employment_check_status: CheckStatus | null
  employment_check_notes: string | null
  landlord_ref_status: CheckStatus | null
  landlord_ref_notes: string | null
  right_to_rent_status: CheckStatus | null
  right_to_rent_notes: string | null
  notes: string | null
  started_at: string | null
  completed_at: string | null
  required_documents: ReferencingDocumentType[] | null
  created_at: string
  updated_at: string
  // Joined
  documents?: ProspectReferencingDocument[]
}

export interface ProspectReferencingDocument {
  id: string
  referencing_id: string
  document_type: ReferencingDocumentType
  document_name: string
  /**
   * Path inside the bucket for this document's type (migration 480):
   * `right-to-rent` for Right to Rent material, `tenant-documents` otherwise.
   */
  storage_path: string
  /** A RESOLVED signed URL, set at read time. Never persisted. */
  file_url?: string
  file_name: string | null
  file_size: number | null
  uploaded_by: string
  notes: string | null
  created_at: string
}

// A room is a unit
// (unit_type='room') since migrations 464-465, so callers pass ONE location in
// `unit_id`. The column still exists and part 3 removes it — until then,
// omitting it from the Insert shape is what stops new rows acquiring one.
// Insert/Update types
// Note: converted_tenancy_id and converted_at are optional - only set during conversion
export type ProspectInsert = Omit<Prospect, 'id' | 'owner_id' | 'user_id' | 'created_at' | 'updated_at' | 'user' | 'viewings' | 'applications' | 'property_interests' | 'converted_tenancy_id' | 'converted_at'> & {
  converted_tenancy_id?: string | null
  converted_at?: string | null
}
export type ProspectUpdate = Partial<ProspectInsert>

export type ProspectViewingInsert = Omit<ProspectViewing, 'id' | 'completed_at' | 'created_at' | 'updated_at' | 'prospect' | 'property' | 'unit'>
export type ProspectViewingUpdate = Partial<ProspectViewingInsert>

export type ProspectApplicationInsert = Omit<ProspectApplication, 'id' | 'reviewed_at' | 'decided_at' | 'created_at' | 'updated_at' | 'prospect' | 'property' | 'unit' | 'referencing'>

export type ProspectPropertyInterestInsert = Omit<ProspectPropertyInterest, 'id' | 'created_at' | 'updated_at' | 'property' | 'unit'>

// Constants
export const PROSPECT_STATUSES: { value: ProspectStatus; label: string; color: string }[] = [
  { value: 'lead', label: 'Enquiry', color: 'zinc' },
  { value: 'viewing', label: 'Viewing', color: 'blue' },
  { value: 'application', label: 'Application', color: 'purple' },
  { value: 'approved', label: 'Approved', color: 'emerald' },
  { value: 'rejected', label: 'Rejected', color: 'red' },
  { value: 'withdrawn', label: 'Withdrawn', color: 'zinc' },
  { value: 'converted', label: 'Converted', color: 'emerald' },
]

export const EMPLOYMENT_STATUSES: { value: EmploymentStatus; label: string }[] = [
  { value: 'employed_full_time', label: 'Employed (Full-time)' },
  { value: 'employed_part_time', label: 'Employed (Part-time)' },
  { value: 'self_employed', label: 'Self-employed' },
  { value: 'retired', label: 'Retired' },
  { value: 'student', label: 'Student' },
  { value: 'unemployed', label: 'Unemployed' },
  { value: 'other', label: 'Other' },
]

export const PROSPECT_REFERRAL_SOURCES: { value: ProspectReferralSource; label: string }[] = [
  { value: 'rightmove', label: 'Rightmove' },
  { value: 'zoopla', label: 'Zoopla' },
  { value: 'openrent', label: 'OpenRent' },
  { value: 'spareroom', label: 'SpareRoom' },
  { value: 'word_of_mouth', label: 'Word of mouth' },
  { value: 'social_media', label: 'Social media' },
  { value: 'website', label: 'Website' },
  { value: 'other', label: 'Other' },
]

export const VIEWING_STATUSES: { value: ViewingStatus; label: string; color: string }[] = [
  { value: 'scheduled', label: 'Scheduled', color: 'blue' },
  { value: 'confirmed', label: 'Confirmed', color: 'emerald' },
  { value: 'declined', label: 'Declined', color: 'red' },
  { value: 'completed', label: 'Completed', color: 'emerald' },
  { value: 'cancelled', label: 'Cancelled', color: 'zinc' },
  { value: 'no_show', label: 'No Show', color: 'red' },
  { value: 'rescheduled', label: 'Rescheduled', color: 'amber' },
]

export const VIEWING_OUTCOMES: { value: ViewingOutcome; label: string; color: string }[] = [
  { value: 'interested', label: 'Interested', color: 'emerald' },
  { value: 'considering', label: 'Considering', color: 'amber' },
  { value: 'not_interested', label: 'Not Interested', color: 'red' },
  { value: 'pending', label: 'Pending Feedback', color: 'zinc' },
]

export const APPLICATION_STATUSES: { value: ApplicationStatus; label: string; color: string }[] = [
  { value: 'submitted', label: 'Submitted', color: 'blue' },
  { value: 'under_review', label: 'Under Review', color: 'amber' },
  { value: 'referencing', label: 'Referencing', color: 'purple' },
  { value: 'approved', label: 'Approved', color: 'emerald' },
  { value: 'rejected', label: 'Rejected', color: 'red' },
  { value: 'withdrawn', label: 'Withdrawn', color: 'zinc' },
]

export const REFERENCING_PROVIDERS: { value: ReferencingProvider; label: string }[] = [
  { value: 'openrent', label: 'OpenRent' },
  { value: 'letting_works', label: 'Letting Works' },
  { value: 'homelet', label: 'HomeLet' },
  { value: 'rightmove', label: 'Rightmove' },
  { value: 'goodlord', label: 'Goodlord' },
  { value: 'vouch', label: 'Vouch' },
  { value: 'manual', label: 'Manual / In-house' },
  { value: 'other', label: 'Other' },
]

export const REFERENCING_STATUSES: { value: ReferencingStatus; label: string; color: string }[] = [
  { value: 'pending', label: 'Pending', color: 'zinc' },
  { value: 'in_progress', label: 'In Progress', color: 'blue' },
  { value: 'passed', label: 'Passed', color: 'emerald' },
  { value: 'failed', label: 'Failed', color: 'red' },
  { value: 'requires_guarantor', label: 'Requires Guarantor', color: 'amber' },
]

export const CHECK_STATUSES: { value: CheckStatus; label: string; color: string }[] = [
  { value: 'pending', label: 'Pending', color: 'zinc' },
  { value: 'passed', label: 'Passed', color: 'emerald' },
  { value: 'failed', label: 'Failed', color: 'red' },
  { value: 'na', label: 'N/A', color: 'zinc' },
]

export const REFERENCING_DOCUMENT_TYPES: { value: ReferencingDocumentType; label: string }[] = [
  { value: 'id_passport', label: 'Passport' },
  { value: 'id_driving_licence', label: 'Driving Licence' },
  { value: 'id_other', label: 'Other ID' },
  { value: 'proof_of_address', label: 'Proof of Address' },
  { value: 'bank_statement', label: 'Bank Statement' },
  { value: 'payslip', label: 'Payslip' },
  { value: 'employment_contract', label: 'Employment Contract' },
  { value: 'landlord_reference', label: 'Landlord Reference' },
  { value: 'credit_report', label: 'Credit Report' },
  { value: 'right_to_rent', label: 'Right to Rent' },
  { value: 'other', label: 'Other' },
]

export const PROPERTY_INTEREST_LEVELS: { value: PropertyInterestLevel; label: string }[] = [
  { value: 'enquired', label: 'Enquired' },
  { value: 'viewing_requested', label: 'Viewing Requested' },
  { value: 'viewing_scheduled', label: 'Viewing Scheduled' },
  { value: 'applied', label: 'Applied' },
  { value: 'offered', label: 'Offered' },
]
